import axios from 'axios'
import handleError from '@/api/errors'
import Events from '@/api/events'

// HTTP GET Request - Returns Resolved or Rejected Promise
export const get = (path) => {
  return new Promise((resolve, reject) => {
    Events.$emit('toggleLoading', true)
    axios.get(path).then(response => {
      Events.$emit('toggleLoading', false)
      resolve(response.data)
    }).catch(error => { handleError(error, path) })
  })
}

// HTTP POST Request - Returns Resolved or Rejected Promise
export const post = (path, data) => {
  return new Promise((resolve, reject) => {
    Events.$emit('toggleLoading', true)
    axios.post(path, data)
      .then(response => {
        Events.$emit('toggleLoading', false)
        resolve(response.data)
      })
      .catch(error => { handleError(error, path) })
  })
}

// HTTP PATCH Request - Returns Resolved or Rejected Promise
export const patch = (path, data) => {
  return new Promise((resolve, reject) => {
    Events.$emit('toggleLoading', true)
    axios.patch(path, data)
      .then(response => {
        Events.$emit('toggleLoading', false)
        resolve(response.data)
      })
      .catch(error => { handleError(error, path) })
  })
}

// HTTP DELETE Request - Returns Resolved or Rejected Promise
export const del = (path) => {
  return new Promise((resolve, reject) => {
    Events.$emit('toggleLoading', true)
    axios.delete(path)
      .then(response => {
        Events.$emit('toggleLoading', false)
        resolve(response.data)
      })
      .catch(error => { handleError(error, path) })
  })
}

export const projectData = (data) => {
  return {
    name: data.name,
    file: data.file,
    file_id: data.file_id,
    file_type: data.product.kind,
    product_id: data.product.id
  }
}

export default {
  // Admin Functions
  //
  adminOrders: (query) => {
    if (query) {
      var queryString = Object.keys(query).map(key => key + '=' + query[key]).join('&')
      return get('/admin/orders.json?' + queryString)
    } else {
      return get('/admin/orders.json')
    }
  },
  cancelOrder: (id) => { return del(`/admin/orders/${id}.json`) },
  reinstateOrder: (id) => { return get(`/admin/orders/${id}/reinstate.json`) },
  replaceOrder: (id) => { return get(`/admin/orders/${id}/replace.json`) },
  redoOrder: (id) => { return get(`/admin/orders/${id}/redo.json`) },

  adminUsers: (query) => {
    if (query) {
      var queryString = Object.keys(query).map(key => key + '=' + query[key]).join('&')
      return get('/admin/users.json?' + queryString)
    } else {
      return get('/admin/users.json')
    }
  },
  adminUser: (id) => {
    return get(`/admin/users/${id}.json`)
  },
  userDisable: (id) => { return del(`/admin/users/${id}.json`) },

  adminUserAddFeatureFlag: (userId, flagName) => {
    return post(`/admin/users/${userId}/feature_flags`, { feature_flag: { feature: flagName.toLowerCase() } })
  },
  adminUserRemoveFeatureFlag: (userId, flagName) => {
    return del(`/admin/users/${userId}/feature_flags/${flagName.toLowerCase()}`)
  },

  // Cart Functions
  //
  cart: () => { return get('/current_cart.json') },
  saveCart: (cart) => {
    var data = {}
    if (cart.order_id) data.order_id = cart.order_id
    if (cart.shipping_method) data.shipping_method = cart.shipping_method
    if (cart.total_shipping) data.shipping_rate = cart.total_shipping
    if (cart.shipping_address) data.shipping_address = cart.shipping_address
    if (cart.billing_address) data.billing_address = cart.billing_address
    if (cart.create_customer) data.create_customer = cart.create_customer

    return patch(`/current_cart.json`, { cart: data })
  },

  // Cart Item Functions
  //
  addCartItem: (id, item) => {
    var data = {}
    if (item.project_id) data.project_id = item.project_id
    if (item.order_item_id) data.order_item_id = item.order_item_id
    if (item.store_item_id) data.store_item_id = item.store_item_id
    if (item.quantity) data.quantity = item.quantity
    if (item.shipping_info) data.shipping_address = item.shipping_info
    if (item.billing_info) data.billing_address = item.billing_info

    return post(`/current_cart/items.json`, { item: data })
  },
  updateCartItem: (id, itemId, item) => {
    var data = {
      project_id: item.project_id,
      order_item_id: item.order_item_id,
      store_item_id: item.store_item_id,
      quantity: item.quantity
    }
    return patch(`/current_cart/items/${itemId}.json`, { item: data })
  },
  removeCartItem: (id, itemId) => {
    return del(`/current_cart/items/${itemId}.json`)
  },
  saveCartAddress: (id, address) => {
    return post(`/current_cart/addresses.json`, address)
  },
  cartShippingMethods: () => {
    return get(`/current_cart/shipping_methods.json`)
  },
  shippingMethods: (destination, itemIds) => {
    return post(`/shipping_methods.json`,
                {address: destination, item_ids: itemIds})
  },

  // Order Functions
  //
  orders: () => { return get('/orders.json') },
  order: (id) => { return get(`/orders/${id}.json`) },
  createOrder: () => {
    return post(`/current_cart/orders.json`)
  },
  payment: (id, data) => {
    return post('/payments.json', data)
  },

  // Item Functions
  //
  items: () => { return get('/items.json') },

  // Product Functions
  //
  products: () => { return get('/products.json') },
  product: (name) => { return get(`/products/${name}.json`) },

  // Project Functions
  projects: () => { return get('/projects.json') },
  project: (id) => { return get(`/projects/${id}.json`) },
  createProject: (data) => {
    return post(`/projects.json`, { project: projectData(data) })
  },
  updateProject: (data) => {
    return patch(`/projects/${data.id}.json`, { project: projectData(data) })
  },
  saveProject: (data) => {
    return post(`/projects.json`, { project: projectData(data) })
  },
  destroyProject: (id) => {
    return del(`/projects/${id}.json`)
  },

  // Store Functions
  //
  newStoreItem: (id, data) => {
    return post(`/store_items.json`, {
      project_id: id,
      store_item: data
    })
  },

  // User Functions
  //
  user: () => { return get(`/current_user.json`) },
  userUpdate: (data) => { return patch(`/current_user.json`, { user: data }) },
  userProducts: (id) => { return get('/current_user/products.json') },

  // User Address Functions
  userAddresses: () => { return get(`/current_user/addresses.json`) },
  deleteAddressInfo: (id) => { return del(`/current_user/addresses/${id}.json`) },
  saveAddressInfo: (address) => {
    if (address.id) {
      return patch(`/current_user/addresses/${address.id}.json`, { address: address })
    } else {
      return post(`/current_user/addresses.json`, { address: address })
    }
  },

  // PDF Functions
  //
  pdf: (id) => { return get(`/pdfs/${id}.json`) },
  createPdf: (data) => { return post(`/pdfs.json`, { pdf: data }) },
  updatePdf: (data) => { return patch(`/pdfs/${data.id}.json`, { pdf: data }) },
  deletePdf: (id) => { return get(`/pdfs/${id}.json`) },

  // Prices
  //
  unitPrices: (data) => {
    return patch(`/pdfs/unit_prices/${data.id}.json`, {
      pdf: {
        cover: data.cover,
        binding: data.binding,
        printing: data.printing,
        ink: data.ink,
        bleed: data.bleed,
        page_count: data.page_count,
        first_page: data.first_page,
        last_page: data.last_page
      }
    })
  },

  // Payment Methods
  //
  paymentMethods: () => { return get('/current_user/payment_methods.json') },
  createPaymentMethod: (pm) => { return post(`/current_user/payment_methods.json`, { payment_method: pm }) },
  savePaymentMethod: (pm) => { return patch(`/current_user/payment_methods/${pm.id}.json`, { payment_method: pm }) },
  deletePaymentMethod: (id) => { return del(`/current_user/payment_methods/${id}.json`) },

  // Reports
  //
  reportsShippedOrders: () => { return get('/reports/shipped_orders.json') },
  reportsUsersAdded: () => { return get('/reports/users_added.json') },

  // Misc Functions
  //
  states: () => { return get('/states.json') },
  countries: () => { return get('/countries.json') },
  loadWebPage: (url) => {
    console.log('Load web page: ', url)
    return post('/load_web_site', { url: url })
  }
}



// WEBPACK FOOTER //
// ./src/api/gungho.js